import { Button } from '@heidi/design-system/button';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from '@heidi/design-system/dropdown-menu';
import { cn } from '@heidi/utils';
import { SlidersHorizontal } from 'lucide-react';
import { defineMessages, useIntl, type MessageDescriptor } from 'react-intl';
import type { ProjectRunFlag, ProjectRunFlags } from '../../contracts/project-run-flags';
import { PROJECT_RUN_OPTION_FLAGS } from './ProjectRunOptionsSelector.utils';

const OPUS_FAST_FLAG = PROJECT_RUN_OPTION_FLAGS.opus48Fast;

const PROJECT_RUN_OPTIONS: readonly ProjectRunOption[] = [
  {
    flag: OPUS_FAST_FLAG,
    shortLabel: {
      id: 'src.features.projects.components.ProjectRunOptionsSelector.opusFastShortLabel',
      defaultMessage: 'Opus fast',
    },
    label: {
      id: 'src.features.projects.components.ProjectRunOptionsSelector.opusFastLabel',
      defaultMessage: 'Opus 4.8 fast mode',
    },
  },
];

const messages = defineMessages({
  triggerDefault: {
    id: 'jwkMyn+5gj',
    defaultMessage: 'Run options',
  },
  multipleSelected: {
    defaultMessage: '{count, plural, one {# option} other {# options}}',
    id: 'XaYJy1mNfv',
  },
  menuLabel: {
    id: 'h7qfcfpXD/',
    defaultMessage: 'Experimental run configuration',
  },
});

interface ProjectRunOption {
  flag: ProjectRunFlag;
  shortLabel: MessageDescriptor;
  label: MessageDescriptor;
}

export interface ProjectRunOptionsSelectorProps {
  value: ProjectRunFlags;
  onChange: (next: ProjectRunFlags) => void;
  disabled?: boolean;
  className?: string;
}

export function ProjectRunOptionsSelector({
  value,
  onChange,
  disabled = false,
  className,
}: ProjectRunOptionsSelectorProps): JSX.Element {
  const intl = useIntl();
  const selectedOptions = PROJECT_RUN_OPTIONS.filter((option) => value[option.flag]);
  const triggerLabel =
    selectedOptions.length === 0
      ? intl.formatMessage(messages.triggerDefault)
      : selectedOptions.length === 1
        ? intl.formatMessage(selectedOptions[0]!.shortLabel)
        : intl.formatMessage(messages.multipleSelected, {
            count: selectedOptions.length,
          });

  const toggleOption = (flag: ProjectRunFlag, checked: boolean) => {
    onChange(checked ? { ...value, [flag]: true } : omitFlag(value, flag));
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild disabled={disabled}>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          disabled={disabled}
          className={cn(
            'text-text-secondary hover:text-text-primary',
            selectedOptions.length > 0 && 'text-text-primary',
            className
          )}
          aria-label={intl.formatMessage(messages.triggerDefault)}
          data-testid="project-run-options-trigger"
        >
          <SlidersHorizontal className="size-4" aria-hidden />
          <span className="truncate">{triggerLabel}</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        className="w-64"
        data-testid="project-run-options-menu"
      >
        <DropdownMenuLabel className="text-xs font-medium text-text-tertiary">
          {intl.formatMessage(messages.menuLabel)}
        </DropdownMenuLabel>
        {PROJECT_RUN_OPTIONS.map((option) => (
          <DropdownMenuCheckboxItem
            key={option.flag}
            checked={!!value[option.flag]}
            onCheckedChange={(checked) => toggleOption(option.flag, checked === true)}
            onSelect={(event) => event.preventDefault()}
            data-testid={`project-run-option-${option.flag}`}
          >
            {intl.formatMessage(option.label)}
          </DropdownMenuCheckboxItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function omitFlag(flags: ProjectRunFlags, flag: ProjectRunFlag): ProjectRunFlags {
  const next = { ...flags };
  delete next[flag];
  return next;
}
